import React, { useCallback, useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import i18n from '@ohif/i18n';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { message } from 'antd';

import { ReactComponent as FR } from '../../assets/flags/fr.svg';
import { ReactComponent as US } from '../../assets/flags/us.svg';
import {
  useMe,
  usePreferenceUser,
  usePreferenceUserUpdate,
} from '../../../helpers/api/users/users';
import { userPreferencesQueryKeys } from '../../../helpers/api/users/users-query-keys';
import {
  getPreferenceLanguageFromApi,
  i18nLanguageToPreference,
  isOwnPreferencesTarget,
  preferenceLanguageToI18n,
  type PreferenceLanguage,
} from '../../../helpers/i18n/languagePreference';
import { Button } from '../../ComponnentsUI/common/Button';

const PreferenceItem = ({ label, children }) => (
  <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
    <p className="text-sm font-medium text-title-page-color-100 dark:text-white">{label}</p>
    <div>{children}</div>
  </div>
);

function applyI18nAndTheme(
  preferenceLanguage: PreferenceLanguage,
  themeValue: 'light' | 'dark'
) {
  const lang = preferenceLanguageToI18n(preferenceLanguage);
  void i18n.changeLanguage(lang);
  localStorage.setItem('i18nextLng', lang);

  const themeAttr = themeValue === 'dark' ? 'deepechodark' : 'deepecho';
  document.documentElement.setAttribute('data-theme', themeAttr);
  localStorage.setItem('theme', themeAttr);
}

const SESSION_DURATION_OPTIONS = [15, 20, 30, 60] as const;

const Preferences = () => {
  const { t } = useTranslation('UserPreferencesModal');
  const queryClient = useQueryClient();
  const params = useParams();
  const { data: me } = useMe();
  const { data: preferencesData } = usePreferenceUser();

  const getThemeWithSystemFallback = () => {
    if (typeof window === 'undefined') {
      return 'deepecho';
    }

    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) {
      return storedTheme;
    }

    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return 'deepechodark';
    }

    return 'deepecho';
  };

  const [theme, setTheme] = useState(getThemeWithSystemFallback);
  const [language, setLanguage] = useState<PreferenceLanguage>(() =>
    i18nLanguageToPreference(
      typeof window !== 'undefined' ? localStorage.getItem('i18nextLng') : null
    )
  );
  const [sessionDuration, setSessionDuration] = useState(30);

  const targetUserId = params.uid ?? me?.uid;
  const editingOwnPreferences = isOwnPreferencesTarget(me, targetUserId);

  useEffect(() => {
    const prefs = preferencesData?.data;
    if (!prefs) return;

    const prefLang = getPreferenceLanguageFromApi(preferencesData);
    if (prefLang) {
      setLanguage(i18nLanguageToPreference(prefLang));
    }
    if (prefs.theme) {
      setTheme(prefs.theme === 'dark' ? 'deepechodark' : 'deepecho');
    }
    if (prefs.session_duration) {
      setSessionDuration(prefs.session_duration);
    }
  }, [preferencesData]);

  const onSuccessEdit = useCallback(
    (response, variables) => {
      message.success(t('SaveMessage'));

      const savedLanguage = (variables?.values?.language ??
        response?.data?.language ??
        language) as PreferenceLanguage;
      const savedTheme = (variables?.values?.theme ?? response?.data?.theme) as
        | 'light'
        | 'dark'
        | undefined;
      const savedUserId = variables?.values?.user_id ?? response?.data?.user_id;

      if (isOwnPreferencesTarget(me, savedUserId ?? targetUserId)) {
        applyI18nAndTheme(
          savedLanguage,
          savedTheme ?? (theme === 'deepechodark' ? 'dark' : 'light')
        );

        queryClient.setQueryData(userPreferencesQueryKeys.detail(), (old) => {
          if (!old || typeof old !== 'object') {
            return old;
          }
          const prev = old as { data?: Record<string, unknown> };
          return {
            ...prev,
            data: {
              ...prev.data,
              language: savedLanguage,
              theme: savedTheme ?? variables?.values?.theme,
              session_duration:
                variables?.values?.session_duration ?? prev.data?.session_duration,
            },
          };
        });
      }
    },
    [t, language, theme, me, targetUserId, queryClient]
  );

  const onErrorEdit = () => {
    message.error(t('SaveFailed'));
  };

  const { mutate: updatePreferences, isPending } = usePreferenceUserUpdate(
    onSuccessEdit,
    onErrorEdit,
    false
  );

  const handleSaveUpdate = () => {
    if (!targetUserId) {
      message.error(t('SaveFailed'));
      return;
    }

    updatePreferences({
      values: {
        user_id: targetUserId,
        theme: theme === 'deepechodark' ? 'dark' : 'light',
        language,
        session_duration: sessionDuration,
      },
    });
  };

  const handleLanguageChange = (newLang: PreferenceLanguage) => {
    setLanguage(newLang);
    if (editingOwnPreferences) {
      applyI18nAndTheme(newLang, theme === 'deepechodark' ? 'dark' : 'light');
    }
  };

  return (
    <div className="space-y-2">
      <PreferenceItem label={t('language')}>
        <div className="flex items-center space-x-2 rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
          <button
            type="button"
            onClick={() => handleLanguageChange('us')}
            className={`flex items-center justify-center p-2 rounded-md transition-colors ${language === 'us' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            aria-label={t('languageEn')}
          >
            <US className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => handleLanguageChange('fr')}
            className={`flex items-center justify-center p-2 rounded-md transition-colors ${language === 'fr' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            aria-label={t('languageFr')}
          >
            <FR className="h-5 w-5" />
          </button>
        </div>
      </PreferenceItem>

      <PreferenceItem label={t('theme')}>
        <div className="flex items-center space-x-2 rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
          <button
            type="button"
            onClick={() => setTheme('deepecho')}
            className={`flex items-center justify-center p-2 rounded-md transition-colors ${theme === 'deepecho' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            aria-label={t('lightTheme')}
          >
            <Sun
              className={`h-5 w-5 ${theme === 'deepecho' ? 'text-blue-600' : 'text-gray-500 dark:text-gray-400'}`}
            />
          </button>
          <button
            type="button"
            onClick={() => setTheme('deepechodark')}
            className={`flex items-center justify-center p-2 rounded-md transition-colors ${theme === 'deepechodark' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            aria-label={t('darkTheme')}
          >
            <Moon
              className={`h-5 w-5 ${theme === 'deepechodark' ? 'text-blue-600' : 'text-gray-500 dark:text-gray-400'}`}
            />
          </button>
        </div>
      </PreferenceItem>

      <PreferenceItem label={t('slotDuration')}>
        <select
          value={sessionDuration}
          onChange={(e) => setSessionDuration(Number(e.target.value))}
          className="rounded-lg bg-gray-100 dark:bg-gray-700 border-none px-3 py-2 text-sm font-medium text-title-page-color-100 dark:text-white outline-none cursor-pointer"
        >
          {SESSION_DURATION_OPTIONS.map((minutes) => (
            <option key={minutes} value={minutes}>
              {t(`slotDuration.${minutes}`)}
            </option>
          ))}
        </select>
      </PreferenceItem>

      <div className="flex justify-end p-2">
        <Button
          type="submit"
          onClick={handleSaveUpdate}
          variant="primary"
          size="sm"
          className="w-fit flex items-center justify-center"
          disabled={isPending || !targetUserId}
        >
          {t('savePreferences')}
          {isPending && <span className="ml-2">{t('saving')}</span>}
        </Button>
      </div>
    </div>
  );
};

export default Preferences;
